import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import TextInput from '@/shared/components/TextInput'
import { Button } from "@/shared/ui/button"
import { FormSelect } from '@/shared/components/FormSelect'
import { selectIntegration } from '@/store/slices/ReportsSlice'
import { useGetReportTemplatesQuery } from '@/api/rtk/reportsApi'
import { toast } from 'react-toastify';
import { useCurrentTool } from '@/shared/hooks/useCurrentTool'
import { initToolFlags, resolveToolFlags, getFormCheckboxes, getCheckboxGridClass } from '@/shared/lib/toolFormFlags'
import { Checkbox } from "@/shared/ui/checkbox"
import { checkIfOnline } from '@/api/imperative/records'
import Loader from '@/shared/components/Loader';
import { useTranslation } from 'react-i18next';
import { reportLanguages, audioLanguages } from '@/shared/config/constants'
import {
  buildTemplateOptions,
  findIntegrationCustomTemplateId,
  resolveIntegrationTemplateId,
} from '@/features/generation/recordFormTemplates'

export default function RecordForm({ GenerateReport, recordedAudioBlob, handleGenerateReport, draftFormData }) {   
    const { t } = useTranslation();
    const { currentToolFields, toolId, toolName, nonAudioTool, tGeneration } = useCurrentTool();
    const { data: templatesData } = useGetReportTemplatesQuery({ toolId }, { skip: !toolId });
    const templates = templatesData?.templates || [];
    const userTemplates = (templatesData?.user_templates || []).filter((tmpl) => String(tmpl?.tool_id) === String(toolId));
    const [loading, setLoading] = useState(false);
    const integration = useSelector(selectIntegration);
    const nameExists = integration.exists && integration.fullName.trim() !== '';

    const toolFields = currentToolFields.reduce((acc, field) => {
      const draftVal = draftFormData?.toolFields?.[field.name];
      acc[field.name] = field.position === 1 && nameExists
        ? integration.fullName
        : draftVal || localStorage.getItem(field.name) || '';
      return acc;
    }, {});

    const [toolFieldState, setToolFieldState] = useState(toolFields);

    const getValidValue = (key, defaultValue) => {
        const value = localStorage.getItem(key);
        return value && value !== 'null' ? value : defaultValue;
    };

    const getValidBoolean = (key, defaultValue = false) => {
        const value = localStorage.getItem(key);
        if (value === null) return defaultValue;
        return value === 'true';
    };

    const [formState, setFormState] = useState({
        audioLanguage: nonAudioTool ? null : (draftFormData?.audioLanguage ?? getValidValue('audio_language', 'de')),
        reportLanguage: draftFormData?.reportLanguage ?? getValidValue('report_language', 'german (switzerland)'),
        comment: draftFormData?.comment ?? '',
        template: draftFormData?.template ?? 'select_template',
        urgent: draftFormData?.urgent ?? false,
        ...initToolFlags(toolName, draftFormData, getValidBoolean),
        isPrivate: draftFormData?.isPrivate ?? false,
    });

    useEffect(() => {
        setFormState((prev) => ({ ...prev, recordedAudioBlob }));
    }, [recordedAudioBlob]);

    const handleToolFieldChange = (e) => {
        const { name, value } = e.target;
        setToolFieldState((prevState) => ({
          ...prevState,
          [name]: value,
        }));
      };
      
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormState((prev) => ({ ...prev, [name]: value }));
    };
    
    const handleDropDownChange = (value) => {
        setFormState((prev)=>({...prev, [value.name]:value.value}))
    }

    const handleGenerateNewReport = async (e) => {
        e.preventDefault();

        setLoading(true);
        const isOnline = await checkIfOnline();
        setLoading(false);
        if(!isOnline){
            toast.error(t('form.errors.networkError'));
            return;
        }
      
        const missingToolFields = currentToolFields.filter(
          (field) => !toolFieldState[field.name]
        ).length;
      
        const { audioLanguage, reportLanguage, template } = formState;
        const toolFlags = resolveToolFlags(toolName, formState);
      
        if (missingToolFields > 0 || (!nonAudioTool && !audioLanguage) || !reportLanguage || !recordedAudioBlob) {
          toast.error(t('form.errors.fillAllFields'));
          return;
        }
        const isCustomTemplate = userTemplates.some(tmpl => String(tmpl.id) === String(template));  
        const toolFieldsData = currentToolFields.map((field) => ({
            id: field.id || '',
            name: field.name,
            value: toolFieldState[field.name],
        }));
      
        const data = {
          toolFieldsData,
          ...formState,
          ...toolFlags,
          uses_custom_template: isCustomTemplate,
          recordedAudioBlob,
        };

        try {
            GenerateReport('generating');
            await handleGenerateReport(data);
        } catch (error) {
            console.error("Error generating report: ", error);
            toast.error(tGeneration('form.errors.generationFailed'));
        }
    };

    const inputLanguages = audioLanguages.map(lang => ({ name: t(`languages.${lang.value}`), value: lang.value })).sort((a, b) => a.name.localeCompare(b.name));
    const outputLanguages = reportLanguages.map(lang => ({ name: t(`languages.${lang.value}`), value: lang.name.toLowerCase() })).sort((a, b) => a.name.localeCompare(b.name));
    const templateOptions = buildTemplateOptions({ templates, userTemplates, t });
    const lockedCustomTemplateId = useMemo(() => {
        if (!integration.exists) return null;
        const scoped = (templatesData?.user_templates || []).filter(
            (tmpl) => String(tmpl?.tool_id) === String(toolId)
        );
        return findIntegrationCustomTemplateId(integration.template, scoped);
    }, [integration.exists, integration.template, templatesData?.user_templates, toolId]);
    const integrationTemplateAppliedRef = useRef(false);

    useEffect(() => {
        if (!integration.exists || !integration.template) {
            integrationTemplateAppliedRef.current = false;
            return;
        }
        if (!templatesData) return;

        const scopedUserTemplates = (templatesData.user_templates || []).filter(
            (tmpl) => String(tmpl?.tool_id) === String(toolId)
        );

        // Custom template id from URL: always keep selected and locked.
        const customId = findIntegrationCustomTemplateId(
            integration.template,
            scopedUserTemplates
        );
        if (customId != null) {
            integrationTemplateAppliedRef.current = true;
            setFormState((prev) =>
                String(prev.template) === String(customId)
                    ? prev
                    : { ...prev, template: customId }
            );
            return;
        }

        // System name match: apply once as a default; user may change afterward.
        if (integrationTemplateAppliedRef.current) return;

        const matchedId = resolveIntegrationTemplateId(integration.template, {
            templates: templatesData.templates || [],
            userTemplates: scopedUserTemplates,
        });
        integrationTemplateAppliedRef.current = true;
        if (matchedId == null) return;

        setFormState((prev) =>
            String(prev.template) === String(matchedId)
                ? prev
                : { ...prev, template: matchedId }
        );
    }, [integration.exists, integration.template, templatesData, toolId]);

    const formCheckboxes = getFormCheckboxes(toolName);

    return (
        <div className='w-full overflow-y-auto pointer-events-auto'>
            <form className='text-left w-full overflow-y-auto pointer-events-auto' onSubmit={handleGenerateNewReport}>
                <div className='mt-[10px] grid grid-cols-2 md:gap-x-[20px] gap-3'>
                {currentToolFields.map((field) => (
                    <TextInput
                        key={field.name}
                        required
                        name={field.name}
                        label={t(`fields.${field.name}`)}
                        placeholder={t(`fields.enter_${field.name}`)}
                        type={field.type || 'text'}
                        value={toolFieldState[field.name]}
                        className={`${field.position === 3 || currentToolFields.length === 1 ? 'col-span-2' : 'col-span-2 md:col-span-1'}`}
                        onChange={handleToolFieldChange}
                    />
                ))}
                    
                </div>
                <div className='mt-[10px] flex flex-col sm:flex-row gap-[20px]'>
                    {!nonAudioTool && (
                        <div className="flex-1">
                            <FormSelect
                                id="record-form-audio-language"
                                label={t('form.inputLanguage')}
                                placeholder={t('form.selectLanguage')}
                                options={inputLanguages}
                                value={formState.audioLanguage}
                                required
                                onValueChange={(v) => handleDropDownChange({ name: 'audioLanguage', value: v })}
                            />
                        </div>
                    )}
                    <div className="flex-1">
                        <FormSelect
                            id="record-form-report-language"
                            label={t('form.outputLanguage')}
                            placeholder={t('form.selectLanguage')}
                            options={outputLanguages}
                            value={formState.reportLanguage}
                            required
                            onValueChange={(v) => handleDropDownChange({ name: 'reportLanguage', value: v })}
                        />
                    </div>
                </div>
                {templateOptions.length > 0 && (
                    <div className="mt-[10px]">
                        <FormSelect
                            id="record-form-template"
                            label={t('form.template')}
                            placeholder={t('form.selectTemplate')}
                            options={templateOptions}
                            required
                            value={formState.template}
                            onValueChange={(v) => handleDropDownChange({ name: 'template', value: v })}
                            className={lockedCustomTemplateId != null ? 'pointer-events-none cursor-default' : undefined}
                            triggerClassName={lockedCustomTemplateId != null ? 'pointer-events-none cursor-default' : undefined}
                        />
                    </div>
                )}
                <div className='mt-[10px] flex flex-col text-sm gap-1 w-full'>
                    <label htmlFor="comment" className="font-semibold font-SuisseIntlLight text-[14px]">{t('common.comment')}</label>
                    <textarea
                        id="comment"
                        name="comment"
                        className="rounded-[10px] inputFieldShadow text-[14px] font-SuisseIntlLight font-normal text-[#868C98] border-[1px] py-2.5 px-4 border-[#E2E4E9] hover:border-[#CED0D5] focus:border-[#A6A8AD] focus:ring-0 focus:outline-none w-full resize-none"
                        rows="3"
                        maxLength='5000'
                        placeholder={t('form.addCommentOptional')}
                        value={formState.comment}
                        onChange={handleChange}
                    />
                </div>
                <div className={`grid ${getCheckboxGridClass(formCheckboxes.length)} gap-3 mt-[14px]`}>
                    {formCheckboxes.map(({ id, labelKey }) => (
                        <div key={id} className="flex text-sm gap-2 items-center">
                            <Checkbox
                                id={id}
                                className={`border-[2.3px] border-slate-200 rounded-[4px] ${formState[id] ? 'border-0' : 'bg-white'}`}
                                checked={formState[id]}
                                onCheckedChange={(checked) => setFormState((prev) => ({ ...prev, [id]: checked }))}
                            />
                            <label htmlFor={id} className="font-medium text-[14px] flex cursor-pointer">
                                <span className="font-semibold font-SuisseIntlLight">{t(labelKey)}</span>
                            </label>
                        </div>
                    ))}
                </div>
                <div className={`sm:flex mt-4`}>
                    <Button type='submit' disabled={loading} className='w-full'>
                        {loading ? 
                            <Loader/>
                        :
                            <span>{tGeneration('form.generate')}</span>

                        }
                    </Button>
                </div>
            </form>
        </div>
    );
}
